import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ComponentType } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap, timeout } from 'rxjs/operators';
import { getPlatformDisplayName, getPlatformLogoPath } from 'src/app/common/platformUtils';
import { NewPlaybooksTriggerComponent } from 'src/app/components/playbooks/new-playbooks-trigger/new-playbooks-trigger.component';
import { PlaybookActionComponent } from 'src/app/components/playbooks/playbook-action/playbook-action.component';
import { PlaybooksTriggerComponent } from 'src/app/components/playbooks/playbooks-trigger/playbooks-trigger.component';
import { isBrownfieldTrigger, isViolationTrigger } from 'src/app/common/playbookUtils';
import { AuthenticationMethod, IntegrationModel, Platform, ResourceType } from 'src/app/models/api';
import { BaseViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { ActionTypes } from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { SendEmailActionDefinitionModel } from 'src/app/models/api/playbooks/actions/sendEmailProperties';
import { handleCopilotAgentNamingConversion, VerticalFilterPage } from 'src/app/common/vertical-filters-utils';
import {
  ActionsResponse,
  Playbook,
  PlaybookAction,
  PlaybookStatus,
  PlaybookTrigger,
} from 'src/app/models/api/playbooks/playbook.model';
import { PlaybookUpdateApiModel } from 'src/app/models/api/playbooks/playbookUpdateApiModel';
import { NewResourceFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newResourceTriggerModel';
import {
  PlaybookCreationApiRequestModel,
  PlaybookCreationApiResponseModel,
} from 'src/app/models/api/playbooks/playbookCreationApiModel';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import {
  PlaybookSupportedActions,
  PlaybooksActionInput,
} from 'src/app/models/componentInputs/playbookActionInput.interface';
import { PlaybooksTriggerInput } from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { PlaybooksActionOutput } from 'src/app/models/popups/outputs/playbookActionOutput.interface';
import { PlaybooksTriggerOutput } from 'src/app/models/popups/outputs/playbooksTriggerOutput.interface';
import { ISelectOption, PlatformSelectOptions } from 'src/app/models/selectOptions';
import { PlaybookStatusSelectOptions } from 'src/app/models/selectOptions/playbookStatusSelectOptions.model';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { arePlatformActionsAllowed } from 'src/app/services/actions/common/actionsHelpers';
import { PlaybooksTriggerActionsService } from 'src/app/services/actions/playbooks/playbooks-trigger-actions/playbooks-trigger-actions.service';
import { ActionsHttpServiceService } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { IntegrationsHttpService } from 'src/app/services/httpServices/integrationsHttpsService/integrations-http.service';
import { PlaybooksHttpServiceService } from 'src/app/services/httpServices/playbooksHttpService/playbooks-http-service.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { PlaybookTemplatesService } from 'src/app/services/templates/playbook/playbook-templates.service';
import { Label } from 'src/app/labels/englishLabels';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-playbook',
  templateUrl: './playbook.component.html',
  styleUrls: ['./playbook.component.scss'],
})
export class PlaybookComponent implements OnInit, OnDestroy {
  public playbook: Playbook;
  public playbookId: string | null;
  public templateId: string | null;
  public integrations: IntegrationModel[];

  public playbookTitle?: string;

  public integrationOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public policyOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public playbookTrigger?: PlaybookTrigger;

  public playbookActions: PlaybookAction[] = [];
  public supportedActionsForSelectedTrigger: Set<PlaybookSupportedActions> = new Set();
  public pageTitle: string;
  public tooltipTriggerDescription: string;
  public generalTriggerDescription: string;

  public waitingForResponse = false;
  public isCopy = false;
  public allowMoreActions = true;
  public backTitle = '';
  private isEdit = false;

  public readonly MAX_AMOUNT_OF_ACTIONS = 20;
  public readonly NAME_MAXIMUM_LENGTH = 50;
  public readonly DESC_MAXIMUM_LENGTH = 500;

  public waitingForActionsResponse: ActionsResponse = ActionsResponse.new;

  public platformOptions$: BehaviorSubject<ISelectOption[]>;

  public playbookStatusOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    PlaybookStatusSelectOptions
  );

  public fg: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required, Validators.maxLength(this.NAME_MAXIMUM_LENGTH)]),
    description: new UntypedFormControl('', [Validators.maxLength(this.DESC_MAXIMUM_LENGTH)]),
    platform: new UntypedFormControl('', Validators.required),
    integration: new UntypedFormControl('', Validators.required),
    policies: new UntypedFormControl([], [Validators.required, Validators.minLength(1)]),
    status: new UntypedFormControl(this._getStatusId(PlaybookStatus.disabled), [Validators.required]),
  });

  public submitAttempt = false;
  public hasPermisions = true;
  public areBrownfieldPlaybooksEnabled = false;
  public integrationValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public policiesValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public platformValid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public policiesDisabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public violationRulesOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  private readonly _platformsWithActionOnAPI = [Platform.workato, Platform.powerplatform];
  private _integrationsSubscription$: Subscription;
  private _baseViolationRulesSubscription$: Subscription;
  private _baseViolationRules: BaseViolationRule[] = [];

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _actionsHttpServiceService: ActionsHttpServiceService,
    private readonly _matDialog: MatDialog,
    private readonly _playbooksTriggerActionsService: PlaybooksTriggerActionsService,
    private readonly _playbooksHttpService: PlaybooksHttpServiceService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _notificationService: NotificationsService,
    private readonly _store: StoreService,
    private readonly _labelProvider: LabelsProviderPipe,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _playbookTemplatesService: PlaybookTemplatesService,
    private readonly _permissionsService: PermissionsService,
    private readonly _logger: LoggerService,
    private readonly _integrationsHttpService: IntegrationsHttpService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _destroyRef: DestroyRef,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  public get allActionsAreValid(): boolean {
    const supportedActionTypes: ActionTypes[] = [];
    this.supportedActionsForSelectedTrigger.forEach((action) => supportedActionTypes.push(action.actionType));

    const updatedSupportedActionTypes =
      this._addEnableDisableAgentActionsToPreserveFormValidation(supportedActionTypes);

    return this.playbookActions.every((action) => updatedSupportedActionTypes.indexOf(action.actionType) >= 0);
  }

  ngOnInit(): void {
    this.platformOptions$ = new BehaviorSubject<ISelectOption[]>(
      PlatformSelectOptions.filter(
        (option) =>
          (option.disabled == undefined || !option.disabled) &&
          [
            Platform.powerplatform,
            Platform.workato,
            Platform.salesforce,
            Platform.powerBI,
            Platform.chatGptEnterprise,
            Platform.amazonBedrock,
            Platform.budibase,
            Platform.airtable,
            Platform.devices,
            Platform.outpost_powerplatform,
            Platform.cursor,
          ].includes(option.id as Platform)
      ).map((option) => {
        if (!this._platformsWithActionOnAPI.includes(<Platform>option.id)) {
          option.tooltip = this._getLimitedPlatformOptionTooltipIfNeeded(<Platform>option.id);
        }
        return option;
      })
    );
    this.playbookId = this._activatedRoute.snapshot.paramMap.get('id');
    this.templateId = this._activatedRoute.snapshot.paramMap.get('templateId');
    this.isCopy = this.playbookId != null && this._router.url.includes('/copy');
    this.isEdit = this.playbookId != null && !this.isCopy;
    this.pageTitle =
      this.playbookId && !this.isCopy
        ? this._labelProvider.transform('playbooks.updatePlaybookText')
        : this._labelProvider.transform('playbooks.createPlaybookText');
    this.backTitle =
      this._labelProvider.transform('common.backTo') +
      ' ' +
      (!this.templateId
        ? this._labelProvider.transform('zenityCommon.playbooks')
        : this._labelProvider.transform('playbooks.marketplace'));
    this.areBrownfieldPlaybooksEnabled = this._featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.BrownfieldPlaybooks
    );

    // on create new playbook - check if user has any operation permission
    // specific integration permission will be checked when integration is selected
    if (!this.playbookId && !this.templateId) {
      this.hasPermisions = this._permissionsService.hasAnyOperationPermission();
      this._createIntegrationOptions();
    }
    // on template or edit - permission will be checked after loading playbook with integration ID
    else {
      this._retrievePlaybookIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this._integrationsSubscription$?.unsubscribe();
    this._baseViolationRulesSubscription$?.unsubscribe();
  }

  public platformOnSelection(option?: ISelectOption) {
    const previousValue = this.fg.controls['platform'].value;
    this._updateSelectControl('platform', option);
    this.platformValid.next(this.fg.controls['platform'].valid);
    if (option && previousValue != option.id) {
      this.playbookTrigger = undefined;
      this.playbookActions = [];
      this.policiesOnClear();
      this.policiesDisabled.next(false);
    }
  }

  public integrationOnSelection(option?: ISelectOption) {
    const previousValue = this.fg.controls['integration'].value;
    const platform = this.integrations.filter((integration) => {
      if (option != undefined) {
        return integration.id == option.id;
      }
      return false;
    })[0].platform;
    const platformSelectionOption = this.platformOptions$.value.filter((option) => option.id == platform)[0];
    this.platformOnSelection(platformSelectionOption);
    this._updateSelectControl('integration', option);
    this.integrationValid.next(this.fg.controls['integration'].valid);

    if (option) {
      // Re-check permission with the selected integration ID
      this.hasPermisions = this._permissionsService.userHasPermission(PERMISSIONS.PLAYBOOK_OPERATION, option.id);

      this._createPolicyOptions(option.id);
      const currentSupportedActions = this.supportedActionsForSelectedTrigger;
      this._updateSupportedActionsBasedOnCurrentSelection();

      if (
        !this._supportedActionsEqual(currentSupportedActions, this.supportedActionsForSelectedTrigger) &&
        !(this.templateId && !previousValue)
      ) {
        this.playbookActions = [];
      }
    }
  }

  public policiesOnSelection(options: ISelectOption[]) {
    this.fg.controls['policies'].setValue(options.map((option) => option.id));
    this.policiesValid.next(this.fg.controls['policies'].valid);
  }

  public statusOnSelection(option?: ISelectOption) {
    this._updateSelectControl('status', option);
  }

  public policiesOnClear() {
    this.fg.controls['policies'].setValue([]);
  }

  public openTrigger() {
    if (
      !this.fg.controls['platform'].valid ||
      !this.fg.controls['integration'].valid ||
      [ActionsResponse.loading, ActionsResponse.edit].includes(this.waitingForActionsResponse)
    ) {
      return;
    }
    const integration = this.integrations.filter(
      (integration) => integration.id == this.fg.controls['integration'].value
    )[0];
    const input = {
      integration: integration,
      trigger: this.playbookTrigger,
      policyIds: this.fg.controls['policies'].value || [],
    } as PlaybooksTriggerInput;

    const needsConfirmation =
      this.areBrownfieldPlaybooksEnabled &&
      this.isEdit &&
      this.playbookTrigger?.triggerType &&
      isBrownfieldTrigger(this.playbookTrigger.triggerType);
    const confirmation$ = needsConfirmation
      ? this._openTriggerEditConfirmationDialog(this.playbookTrigger!.triggerType)
      : of(true);

    confirmation$
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() => {
          const playbooksTriggerComponent: ComponentType<NewPlaybooksTriggerComponent | PlaybooksTriggerComponent> =
            this.areBrownfieldPlaybooksEnabled ? NewPlaybooksTriggerComponent : PlaybooksTriggerComponent;

          const dialogRef = this._matDialog.open(playbooksTriggerComponent, {
            disableClose: false,
            width: '850px',
            height: '600px',
            panelClass: 'sidepanel-dialog-container',
            data: input,
          });

          return dialogRef.afterClosed();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((output: PlaybooksTriggerOutput) => {
        if (output.trigger && output.trigger != this.playbookTrigger) {
          const previousTrigger = this.playbookTrigger;
          this.playbookTrigger = output.trigger;
          this.waitingForActionsResponse = ActionsResponse.new;
          this._updateSupportedActionsBasedOnCurrentSelection();

          const isFindingOrIssueTrigger = this._isFindingOrIssueTrigger(this.playbookTrigger);
          const wasFindingOrIssueTrigger = this._isFindingOrIssueTrigger(previousTrigger);

          if (isFindingOrIssueTrigger && !wasFindingOrIssueTrigger) {
            this._autoSelectDefaultPolicyForFindingTrigger();
          }

          this.policiesDisabled.next(isFindingOrIssueTrigger);
        }
      });
  }

  public openAction(actionIndex?: number) {
    // Early return if conditions are not met
    if (!this.playbookTrigger || this.waitingForActionsResponse !== ActionsResponse.success) {
      return;
    }

    let action: PlaybookAction | undefined;

    if (actionIndex != undefined) {
      action = this.playbookActions[actionIndex];
    }

    const integration = this.integrations.filter(
      (integration) => integration.id == this.fg.controls['integration'].value
    )[0];
    let actionTooltip = this._getLimitedPlatformOptionTooltipIfNeeded(integration.platform);
    if (actionTooltip == undefined) {
      actionTooltip = this._getLimitedIntegrationOptionTooltipIfNeeded(integration);
    }

    const input = {
      platform: this.fg.controls['platform'].value,
      action: action,
      trigger: this.playbookTrigger,
      supportedActions: Array.from(this.supportedActionsForSelectedTrigger),
      limitedActionsTooltip: actionTooltip,
    } as PlaybooksActionInput;

    const dialogRef = this._matDialog.open(PlaybookActionComponent, {
      disableClose: false,
      width: '850px',
      height: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: input,
    });

    dialogRef.afterClosed().subscribe((output: PlaybooksActionOutput) => {
      if (output.action) {
        if (actionIndex != undefined) {
          this.playbookActions[actionIndex] = output.action;
        } else {
          this.playbookActions.push(output.action);
        }
        // Manually trigger change detection to ensure UI updates immediately
        this._cdr.detectChanges();
      }
    });
  }

  public removeAction(actionIndex: number) {
    this.playbookActions.splice(actionIndex, 1);
    // Manually trigger change detection to ensure UI updates immediately
    this._cdr.detectChanges();
  }

  public createOrSave() {
    this.integrationValid.next(this.fg.controls['integration'].valid);
    this.policiesValid.next(this.fg.controls['policies'].valid);
    this.platformValid.next(this.fg.controls['platform'].valid);

    if (this.fg.invalid || !this.playbookTrigger || this.playbookActions.length == 0 || !this.allActionsAreValid) {
      this.submitAttempt = true;
      return;
    }

    this.submitAttempt = false;
    this.waitingForResponse = true;

    const updatingPlaybook = this.playbookId && !this.isCopy;

    const playbookId$ = updatingPlaybook
      ? this._updatePlaybook(this.playbookId!).pipe(
          tap(() => this._notificationService.success('Playbook updated')),
          map(() => this.playbookId!)
        )
      : this._createPlaybook().pipe(
          tap(() => this._notificationService.success('Playbook created')),
          map((response: PlaybookCreationApiResponseModel) => response.playbookId)
        );

    playbookId$
      .pipe(
        switchMap((playbookId) => {
          if (
            this.areBrownfieldPlaybooksEnabled &&
            isBrownfieldTrigger(this.playbookTrigger!.triggerType) &&
            this._isPlaybookActive()
          ) {
            return this._triggerPlaybook(playbookId).pipe(
              tap(() => this._notificationService.success('Playbook triggered'))
            );
          }

          return of(null);
        })
      )
      .subscribe({
        next: () => {
          this.waitingForResponse = false;
          this._router.navigateByUrl('app/playbooks');
        },
        error: (httpError: HttpErrorResponse) => {
          if (httpError.status === 422) {
            this._notificationService.error(httpError.error.detail);
          } else {
            this._httpErrorHandlerService.handle(httpError);
          }

          this.waitingForResponse = false;
        },
      });
  }

  public actionValidForTrigger(action: PlaybookAction): boolean {
    const supportedActionTypes: ActionTypes[] = [];
    this.supportedActionsForSelectedTrigger.forEach((action) => supportedActionTypes.push(action.actionType));

    const updatedSupportedActionTypes =
      this._addEnableDisableAgentActionsToPreserveFormValidation(supportedActionTypes);

    return updatedSupportedActionTypes.indexOf(action.actionType) >= 0;
  }

  public clickOnBack() {
    if (!this.templateId) {
      this._router.navigateByUrl('app/playbooks');
    } else {
      this._router.navigateByUrl('app/playbooks?tab=Marketplace');
    }
  }

  public actionValuesAreValid(action: PlaybookAction): boolean {
    if (action.actionType == ActionTypes.sendEmail) {
      const props = action.actionDefinitionProperties as SendEmailActionDefinitionModel;
      const hasRecipients = (props.recipients ?? []).length > 0;
      const makerChecked = props.contactMaker === true;
      const actorChecked = props.contactActor === true;
      const isFindingOrIssueTrigger = this._isFindingOrIssueTrigger(this.playbookTrigger);

      // For runtime findings and issues: allow recipients OR contactActor
      if (isFindingOrIssueTrigger) {
        if (!hasRecipients && !actorChecked) {
          return false;
        }
      } else {
        // For non-finding triggers (inventory/resource): allow recipients OR contactMaker
        if (!hasRecipients && !makerChecked) {
          return false;
        }
      }
    }

    return true;
  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.playbookActions, event.previousIndex, event.currentIndex);
  }

  private async _updateSupportedActionsBasedOnCurrentSelection() {
    if (
      this.fg.controls['platform'].valid &&
      this.playbookTrigger &&
      this.fg.controls['integration'].valid &&
      this.integrations
    ) {
      const previousState = this.waitingForActionsResponse;
      if (this.waitingForActionsResponse != ActionsResponse.edit) {
        this.waitingForActionsResponse = ActionsResponse.loading;
        // Manually trigger change detection to show loader immediately
        this._cdr.detectChanges();
      }
      const integration = this.integrations.filter(
        (integration) => integration.id == this.fg.controls['integration'].value
      );
      this._actionsHttpServiceService
        .getActionsOfPlaybookTrigger(integration[0].id, this.playbookTrigger)
        .pipe(
          timeout(30000), // 30 second timeout
          catchError((error) => {
            // Handle timeout errors - timeout operator throws an error with name 'TimeoutError'
            if (error?.name === 'TimeoutError' || error?.message?.includes('timeout')) {
              this._logger.error('Timeout loading actions for playbook trigger after 30 seconds');
              return throwError(
                () =>
                  new HttpErrorResponse({
                    error: 'Request timeout',
                    status: 408,
                    statusText: 'Request Timeout',
                  })
              );
            }
            return throwError(() => error);
          }),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe({
          next: (actions) => {
            this.supportedActionsForSelectedTrigger = this._playbooksTriggerActionsService.setActionsDisabledAndTooltip(
              integration[0],
              actions.items,
              this.playbookTrigger
            );
            this.waitingForActionsResponse = ActionsResponse.success;
            // Manually trigger change detection to ensure UI updates immediately
            this._cdr.detectChanges();
          },
          error: (error: HttpErrorResponse) => {
            this._logger.error('Failed to load actions for playbook trigger', error);
            this._httpErrorHandlerService.handle(error);
            // Reset to previous state (preserve edit mode, otherwise reset to new to allow retry)
            this.waitingForActionsResponse =
              previousState === ActionsResponse.edit ? ActionsResponse.edit : ActionsResponse.new;
            // Manually trigger change detection to ensure UI updates immediately
            this._cdr.detectChanges();
          },
        });
    }
  }

  private _createPlaybook(): Observable<PlaybookCreationApiResponseModel> {
    const newPlaybook = {
      name: this.fg.controls['name'].value,
      description: this.fg.controls['description'].value,
      status: this._getStatusValue(this.fg.controls['status'].value),
      platform: this.fg.controls['platform'].value,
      integrationId: this.fg.controls['integration'].value,
      trigger: this.playbookTrigger,
      actions: this.playbookActions,
      policyIds: this.fg.controls['policies'].value,
    } as PlaybookCreationApiRequestModel;

    return this._playbooksHttpService.createPlaybook(newPlaybook);
  }

  private _updatePlaybook(playbookId: string): Observable<string> {
    const update = {
      name: this.fg.controls['name'].value,
      description: this.fg.controls['description'].value,
      trigger: this.playbookTrigger,
      actions: this.playbookActions,
      policyIds: this.fg.controls['policies'].value,
      status: this._getStatusValue(this.fg.controls['status'].value),
    } as PlaybookUpdateApiModel;

    const integrationId = this.fg.controls['integration'].value;
    return this._integrationsHttpService.getIntegration(integrationId).pipe(
      map((integration) => {
        const playbookUpdatePayload = structuredClone(update);

        // Only process resourceTypes for resource triggers, not finding triggers
        if (playbookUpdatePayload.trigger.triggerType === TriggerTypes.newResourceFound) {
          let resourceTypes: string[] =
            structuredClone(
              (playbookUpdatePayload.trigger.configuration as NewResourceFoundTriggerConfigModel).resourceTypes
            ) || [];
          resourceTypes =
            handleCopilotAgentNamingConversion(VerticalFilterPage.Playbooks, resourceTypes || [], [
              integration.platform,
            ]) || [];
          (playbookUpdatePayload.trigger.configuration as NewResourceFoundTriggerConfigModel).resourceTypes =
            resourceTypes as ResourceType[];
        }

        return playbookUpdatePayload;
      }),
      switchMap((update) => this._playbooksHttpService.updatePlaybook(playbookId, update))
    );
  }

  private _isPlaybookActive(): boolean {
    return this._getStatusValue(this.fg.controls['status'].value) === PlaybookStatus.active;
  }

  private _triggerPlaybook(playbookId: string): Observable<void> {
    return this._playbooksHttpService.triggerPlaybook(playbookId);
  }

  private _updateSelectControl(controlName: string, option?: ISelectOption) {
    if (option) {
      this.fg.controls[controlName].setValue(option.id);
    } else {
      this.fg.controls[controlName].setValue(undefined);
    }
  }

  private _retrievePlaybookIfNeeded() {
    if (this.playbookId) {
      this.waitingForActionsResponse = ActionsResponse.edit;
      this._playbooksHttpService.getPlaybook(this.playbookId).subscribe(
        (playbook) => {
          this._putPlaybookValuesInPage(playbook);
        },
        (error) => {
          this._httpErrorHandlerService.handle(error);
          if (error.status === 404) {
            this.clickOnBack();
          }
        }
      );
    } else if (this.templateId) {
      this.waitingForActionsResponse = ActionsResponse.template;
      this._playbookTemplatesService.generatePlaybookFromTemplateId(this.templateId).subscribe((playbook) => {
        if (playbook) {
          this._putPlaybookTemplateInPage(playbook);
        }
      });
    }
  }

  private _putPlaybookValuesInPage(playbook: Playbook) {
    this.playbook = playbook;
    if (this.isCopy) {
      this.playbook.name = this.playbook.name + ' - Copy';
    }

    if (this.isEdit) {
      this.hasPermisions = this._permissionsService.userHasPermission(
        PERMISSIONS.PLAYBOOK_OPERATION,
        playbook.integration.id
      );
    } else {
      this.hasPermisions = this._permissionsService.userHasPermission(PERMISSIONS.PLAYBOOK_OPERATION);
    }

    this.fg.controls['name'].setValue(playbook.name);
    this.fg.controls['platform'].setValue(playbook.platform);
    this.fg.controls['integration'].setValue(playbook.integration.id);
    this.fg.controls['description'].setValue(playbook.description);
    this.fg.controls['policies'].setValue(playbook.policies.map((policy) => policy.id));

    // status is initialized to disabled
    // we want to keep it disabled on new playbook creation(copy) by default
    if (!this.isCopy) {
      this.fg.controls['status'].setValue(
        this._getStatusId(playbook.status) || this._getStatusId(PlaybookStatus.disabled)
      );
    }

    this.playbookTrigger = playbook.trigger;
    this.playbookActions = playbook.actions;

    const isFindingOrIssueTrigger = this._isFindingOrIssueTrigger(this.playbookTrigger);
    this.policiesDisabled.next(isFindingOrIssueTrigger);

    if (this.playbookTrigger.triggerType === TriggerTypes.newViolationFound) {
      const config = this.playbookTrigger.configuration as NewViolationFoundTriggerConfigModel;
      this._baseViolationRulesSubscription$ = this._store.baseViolationRule$.subscribe((rules) => {
        this._baseViolationRules = rules;
        const ruleById = _.keyBy(this._baseViolationRules, 'id');

        // Remove deleted rules from the selected rules
        const nonDeletedRuleIdsFromConfig = new Set(
          this._baseViolationRules.filter((rule) => config.ruleIds?.includes(rule.id)).map((rule) => rule.id)
        );
        if (config.ruleIds) {
          // Not all the trigger violation rules were deleted
          if (nonDeletedRuleIdsFromConfig.size > 0) {
            const deletedRulesIdsFromConfig = config.ruleIds.filter((id) => !nonDeletedRuleIdsFromConfig.has(id));
            this._logger.info(
              `Playbook ${playbook.id} contains deleted rules ${deletedRulesIdsFromConfig.join(', ')}, Updating playbook`
            );
            config.ruleIds = Array.from(nonDeletedRuleIdsFromConfig);
          }
          // All the trigger violation rules were deleted
          if (nonDeletedRuleIdsFromConfig.size == 0) {
            this._logger.info(`Playbook ${playbook.id} contains only deleted rules ${config.ruleIds.join(', ')}`);
            // TODO - Fix on behalf of issue web-app #2627
          }
        }

        const selectedRules = Array.from(nonDeletedRuleIdsFromConfig).map((id) => ruleById[id]?.displayName);
        if (nonDeletedRuleIdsFromConfig.size > 2) {
          this.generalTriggerDescription =
            Array.from(selectedRules).slice(0, 1) + ' and ' + String(selectedRules.length - 1) + ' others';
        } else {
          this.generalTriggerDescription = Array.from(selectedRules).join(', ');
        }
      });
      if (config.severity) {
        const severity = 'Severity: ' + config.severity + '  ' + '\n';
        this._enrichTriggerDescription(severity);
      }
      if (config.resourceBusinessCriticality) {
        const businessCriticality = 'Business criticality: ' + config.resourceBusinessCriticality + '  ' + '\n';
        this._enrichTriggerDescription(businessCriticality);
      }
      if (config.resourceCreatedBy) {
        const createdBy = 'Created by: ' + config.resourceCreatedBy + '  ' + '\n';
        this._enrichTriggerDescription(createdBy);
      }
      if (config.resourceStatus) {
        const createdBy = 'Status: ' + config.resourceStatus + '  ' + '\n';
        this._enrichTriggerDescription(createdBy);
      }
      if (config.resourceSecurityRisk) {
        const createdBy = 'Security risk: ' + config.resourceSecurityRisk + '  ' + '\n';
        this._enrichTriggerDescription(createdBy);
      }
      if (config.resourceLastModifiedBy) {
        const createdBy = 'Last modified by: ' + config.resourceLastModifiedBy + '  ' + '\n';
        this._enrichTriggerDescription(createdBy);
      }
      if (this.tooltipTriggerDescription) {
        this.tooltipTriggerDescription = 'Filtered by: \n' + this.tooltipTriggerDescription;
      }
    }

    this._createIntegrationOptions(playbook.platform, this.fg.controls['integration'].value || undefined);
    this.allowMoreActions = this.playbookActions.length < this.MAX_AMOUNT_OF_ACTIONS;
  }

  private _putPlaybookTemplateInPage(playbook: PlaybookCreationApiRequestModel) {
    // Template is create flow - check permission without integration ID
    this.hasPermisions = this._permissionsService.userHasPermission(PERMISSIONS.PLAYBOOK_OPERATION);

    this.fg.controls['name'].setValue(playbook.name);
    this.fg.controls['platform'].setValue(playbook.platform);
    this.fg.controls['integration'].setValue(playbook.integrationId);
    this.fg.controls['description'].setValue(playbook.description);
    this.fg.controls['policies'].setValue(playbook.policyIds);
    this.fg.controls['status'].setValue(
      this._getStatusId(playbook.status) || this._getStatusId(PlaybookStatus.disabled)
    );
    this.playbookTrigger = playbook.trigger;
    this.playbookActions = playbook.actions;

    const isFindingOrIssueTrigger = this._isFindingOrIssueTrigger(this.playbookTrigger);
    this.policiesDisabled.next(isFindingOrIssueTrigger);

    if (!playbook.integrationId) {
      this.integrationValid.next(false);
    }

    this._createIntegrationOptions(playbook.platform, playbook.integrationId);

    this.allowMoreActions = this.playbookActions.length < this.MAX_AMOUNT_OF_ACTIONS;
  }

  private _enrichTriggerDescription(data: string) {
    if (this.tooltipTriggerDescription) {
      this.tooltipTriggerDescription = this.tooltipTriggerDescription + data;
    } else {
      this.tooltipTriggerDescription = data;
    }
  }

  private _createIntegrationOptions(platform?: string, integrationId?: string) {
    const integrationSelectOptions: ISelectOption[] = [];
    const supportedPlatforms = this.platformOptions$.value.map((option) => option.id);

    this._integrationsSubscription$ = this._store.integrations$.subscribe((integrations) => {
      this.integrations = integrations;
      integrations.forEach((integration) => {
        if (
          (supportedPlatforms.indexOf(integration.platform) > -1 && platform == undefined) ||
          integration.platform == platform
        ) {
          const disabled = integration.connectionStatus.code == StatusCodeEnum.on ? false : true;
          const displayNameStatusSuffix =
            integration.connectionStatus.code == StatusCodeEnum.on
              ? this._labelProvider.transform('integrationStatus.activeStatus')
              : this._labelProvider.transform('integrationStatus.inactiveStatus');
          integrationSelectOptions.push({
            id: integration.id,
            value: integration.id,
            displayName: integration.name + ' (' + displayNameStatusSuffix + ')',
            disabled: disabled,
            tooltip: this._getLimitedIntegrationOptionTooltipIfNeeded(integration),
            iconPath: getPlatformLogoPath(integration.platform),
          } as ISelectOption);
        }
      });
      this.integrationOptions$.next(integrationSelectOptions.sort((a, b) => this._sortIntegrationsOptions(a, b)));

      if (integrationId == undefined) {
        const activeIntegrationOptions = this.integrationOptions$.value.filter((opt) => !opt.disabled);
        if (activeIntegrationOptions.length == 1) {
          this.integrationOnSelection(activeIntegrationOptions[0]);
        } else {
          this._updateSelectControl('integration');
        }
      } else {
        if (this.fg.controls['integration'].value) {
          this._updateSupportedActionsBasedOnCurrentSelection();
          this._createPolicyOptions(this.fg.controls['integration'].value, true);
        }
      }
    });
  }

  private _createPolicyOptions(integrationId: string, isEdit = false) {
    this._policiesHttpService.getIntegrationPolicies(integrationId).subscribe((policies) => {
      const policiesSelectionOptions: ISelectOption[] = policies.map(
        (policy) =>
          ({
            id: policy.id,
            value: policy.id,
            displayName: policy.name,
          }) as ISelectOption
      );
      this.policyOptions$.next(policiesSelectionOptions);

      if (!isEdit) {
        const isFindingOrIssueTrigger = this._isFindingOrIssueTrigger(this.playbookTrigger);

        if (isFindingOrIssueTrigger) {
          const defaultPolicy = policies.find((policy) => policy.isDefault);
          if (defaultPolicy) {
            const defaultPolicyOption = policiesSelectionOptions.find((option) => option.id === defaultPolicy.id);
            if (defaultPolicyOption) {
              this.policiesOnSelection([defaultPolicyOption]);
            }
          }
        } else if (policies.length == 1) {
          this.policiesOnSelection(policiesSelectionOptions);
        } else if (this.fg.controls['policies'].value?.length > 0) {
          this._updateSelectControl('policies');
        }
      }
    });
  }

  private _autoSelectDefaultPolicyForFindingTrigger() {
    const integrationId = this.fg.controls['integration'].value;
    if (!integrationId) {
      this._logger.error('Cannot auto-select default policy: integration ID is missing');
      return;
    }

    this._policiesHttpService.getIntegrationPolicies(integrationId).subscribe((policies) => {
      const defaultPolicy = policies.find((policy) => policy.isDefault);
      if (defaultPolicy) {
        const policiesSelectionOptions = this.policyOptions$.value;
        const defaultPolicyOption = policiesSelectionOptions.find((option) => option.id === defaultPolicy.id);
        if (defaultPolicyOption) {
          this.policiesOnSelection([defaultPolicyOption]);
        }
      }
    });
  }

  private _getLimitedPlatformOptionTooltipIfNeeded(platform: Platform) {
    if (!this._platformsWithActionOnAPI.includes(platform)) {
      return `Limited playbooks for ${getPlatformDisplayName(platform)}.`;
    }
    return undefined;
  }

  private _getLimitedIntegrationOptionTooltipIfNeeded(integration: IntegrationModel) {
    if (!arePlatformActionsAllowed(integration.platform, integration.authenticationMethod)) {
      // TODO: change message per platform once relevant
      return `Limited playbooks for ${integration.platform} using ${integration.authenticationMethod} authentication method, switch to ${AuthenticationMethod.apikey} or ${AuthenticationMethod.apitoken} to get more actions`;
    }
    return undefined;
  }

  private _getStatusValue(id: string) {
    const option = PlaybookStatusSelectOptions.filter((option) => option.id == id)[0];
    return option?.value || PlaybookStatus.disabled;
  }

  private _getStatusId(status: PlaybookStatus) {
    const option = PlaybookStatusSelectOptions.filter((option) => option.value == status)[0];
    return option?.id;
  }

  private _supportedActionsEqual(setA: Set<PlaybookSupportedActions>, setB: Set<PlaybookSupportedActions>) {
    const actionsSetA: Set<ActionTypes> = new Set<ActionTypes>();
    const actionsSetB: Set<ActionTypes> = new Set<ActionTypes>();
    setA.forEach((action) => {
      if (!action.disabled) {
        actionsSetA.add(action.actionType);
      }
    });
    setB.forEach((action) => {
      if (!action.disabled) {
        actionsSetB.add(action.actionType);
      }
    });
    if (actionsSetA.size != actionsSetB.size) {
      return false;
    }
    return Array.from(actionsSetA).every((element) => actionsSetB.has(element));
  }

  private _sortIntegrationsOptions(a: ISelectOption, b: ISelectOption) {
    if (a.disabled && b.disabled) {
      return 0;
    }
    if (!a.disabled && b.disabled) {
      return -1;
    }
    return 1;
  }

  private _addEnableDisableAgentActionsToPreserveFormValidation(supportedActionTypes: ActionTypes[]): ActionTypes[] {
    if (!supportedActionTypes.includes(ActionTypes.enableAgentAction)) {
      supportedActionTypes.push(ActionTypes.enableAgentAction);
    }
    if (!supportedActionTypes.includes(ActionTypes.disableAgentAction)) {
      supportedActionTypes.push(ActionTypes.disableAgentAction);
    }

    return supportedActionTypes;
  }

  private _openTriggerEditConfirmationDialog(triggerType: TriggerTypes): Observable<boolean> {
    const dialogRef = this._matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: Label.get('playbooks.updatePlaybookTriggerText'),
        content: isViolationTrigger(triggerType)
          ? Label.get('playbooks.violationTriggerEditConfirmationMessage')
          : Label.get('playbooks.resourceTriggerEditConfirmationMessage'),
        icon: 'settings-2',
      },
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });

    return dialogRef.afterClosed().pipe(map((userConfirmed?: boolean) => userConfirmed === true));
  }

  private _isFindingTrigger(trigger?: PlaybookTrigger): boolean {
    return trigger?.triggerType === TriggerTypes.newFindingFound;
  }

  private _isIssueTrigger(trigger?: PlaybookTrigger): boolean {
    return trigger?.triggerType === TriggerTypes.newIssueFound;
  }

  private _isFindingOrIssueTrigger(trigger?: PlaybookTrigger): boolean {
    return this._isFindingTrigger(trigger) || this._isIssueTrigger(trigger);
  }
}
